import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import {
  TOUR_STATUS_SCHEDULED,
  TOUR_SHOWING_STATUS_CONFIRMED,
  TOUR_SHOWING_STATUS_COMPLETED,
  TOUR_SHOWING_STATUS_FAILED,
  TOUR_SHOWING_APPLICATION_SOURCE_RECOMMENDED_PAGE_TOURS,
} from 'common/tourConstants';

import { sortToursByStartOrCreatedDate } from 'common/tourHelpers';
import { formatPhoneNumber } from 'common/formatters';
import ButtonDeprecated, { BUTTON_THEME_BLUE } from 'client/components/ButtonDeprecated';
import SrTourListRow from 'client/components/SrTourListRow';
import SrTourUpcomingStatus from 'client/components/SrTourUpcomingStatus';
import SrTourDate from 'client/components/SrTourDate';
import SrTourArchiveNote from 'client/components/SrTourArchiveNote';
import SrTourListRowEditModal from 'client/components/SrTourListRowEditModal';
import SrTourListRowRemoveConfirmationModal from 'client/components/SrTourListRowRemoveConfirmationModal';
import SrTourRescheduleModal from 'client/components/SrTourRescheduleModal';
import SrLink from 'client/components/SrLink/SrLink';
import SrEducationalTourCard from 'client/components/SrEducationalTourCard';

/**
 * Display the notAvailable tour listings to the bottom of the tour group
 */
function sortListingByTourStatus(listings) {
  return listings.sort((listingA, listingB) => {
    if (listingA.tourListingStatus === TOUR_SHOWING_STATUS_FAILED) {
      return 1;
    }
    if (listingB.tourListingStatus === TOUR_SHOWING_STATUS_FAILED) {
      return -1;
    }
    return 0;
  });
}

class SrTourList extends Component {
  static propTypes = {
    tourSections: PropTypes.array.isRequired,
    highlightListingId: PropTypes.number,
    subNav: PropTypes.node,
    listingPhotoResolution: PropTypes.number.isRequired,
    userAgent: PropTypes.object.isRequired,
    currentUser: PropTypes.object.isRequired,
  };

  constructor(props) {
    super(props);

    this.state = {
      showEditModal: false,
      showRemoveConfirmationModal: false,
      editProps: null,
      showRescheduleTourModal: false,
      rescheduleProps: null,
    };
  }

  _onEditRow = (editProps) => {
    this.setState({
      showEditModal: true,
      editProps,
    });
  };

  _onCloseEditModal = () => {
    this.setState({
      showEditModal: false,
      editProps: null,
    });
  };

  _onOpenRemoveConfirmationModal = (editProps) => {
    this.setState({
      showEditModal: false,
      showRemoveConfirmationModal: true,
      editProps,
    });
  };

  _onCloseRemoveConfirmationModal = () => {
    this.setState({
      showRemoveConfirmationModal: false,
      editProps: null,
    });
  };

  _onOpenRescheduleTourModal = (rescheduleProps) => {
    this.setState({
      showRescheduleTourModal: true,
      rescheduleProps,
    });
  };

  _onCloseRescheduleTourModal = () => {
    this.setState({
      showRescheduleTourModal: false,
      rescheduleProps: null,
    });
  };

  render() {
    const {
      tourSections,
      highlightListingId,
      subNav,
      listingPhotoResolution,
      userAgent,
      currentUser,
    } = this.props;

    const {
      showEditModal,
      showRemoveConfirmationModal,
      editProps,
      showRescheduleTourModal,
      rescheduleProps,
    } = this.state;

    let listingNumberToSee = 0;

    const sectionsAreEmpty = tourSections.every(({ tours }) => tours.length === 0);

    return (
      <div className="toursContainer clearfix">
        <div>
          {tourSections.map((tourSection) => (
            <div className="tourSection" key={tourSection.headline}>
              <span className="headline main">
                <span className="headlineText">{tourSection.headline}</span>
                <a
                  href={`sms:+1${currentUser.customerSupportPhone}`}
                  className="blue-bright weight-medium inline font-normal"
                >
                  {`Help: ${formatPhoneNumber(currentUser.customerSupportPhone)}`}
                </a>
              </span>
              {subNav}
              <div className="resultsContainer clearfix">
                {sectionsAreEmpty && <SrEducationalTourCard />}
                {sortToursByStartOrCreatedDate(tourSection.tours).map((tour) => {
                  const listings = sortListingByTourStatus([...tour.listings]);
                  const recommendedListings = tourSection.recommendedListings
                    ? tourSection.recommendedListings
                    : null;
                  const showRescheduleComponent =
                    tourSection.isUpcoming && (tour.markedNeedsReschedulingFlag || !tour.startDate);
                  const homePluralize =
                    recommendedListings && recommendedListings.length > 1
                      ? 'these homes'
                      : 'this home';
                  return (
                    <div
                      key={tour.id}
                      className={classnames('tourSelfScheduleContainer', {
                        reschedule: showRescheduleComponent,
                      })}
                    >
                      {tourSection.isUpcoming && (
                        <SrTourUpcomingStatus
                          tour={{
                            ...tour,
                            listings,
                          }}
                          onRescheduleTour={this._onOpenRescheduleTourModal}
                        />
                      )}
                      {tourSection.isCompleted && (
                        <div className="tourStatusContainer completed">
                          <SrTourDate tour={tour} />
                        </div>
                      )}
                      {tourSection.isArchived && (
                        <div className="tourStatusContainer archived">
                          <SrTourArchiveNote tour={tour} />
                        </div>
                      )}
                      <div className="tourRowsContainer">
                        {listings.map((listing) => {
                          if (
                            tour.status === TOUR_STATUS_SCHEDULED &&
                            [TOUR_SHOWING_STATUS_CONFIRMED, TOUR_SHOWING_STATUS_COMPLETED].includes(
                              listing.tourListingStatus,
                            )
                          ) {
                            listingNumberToSee += 1;
                          }

                          return (
                            <SrTourListRow
                              key={`tour-${tour.id}-listing-${listing.id}`}
                              listingNumberToSee={listingNumberToSee}
                              listing={listing}
                              tour={tour}
                              onEdit={this._onEditRow}
                              onConfirmRemoveLastListing={this._onOpenRemoveConfirmationModal}
                              onClose={this._onCloseEditModal}
                              highlight={
                                tourSection.isUpcoming && listing.id === highlightListingId
                              }
                              listingPhotoResolution={listingPhotoResolution}
                              userAgent={userAgent}
                            />
                          );
                        })}
                      </div>

                      {showRescheduleComponent && (
                        <div className="rescheduleContainer">
                          <ButtonDeprecated
                            theme={BUTTON_THEME_BLUE}
                            onClick={this._onRescheduleButtonClick}
                            href={`/tour/${tour.id}/${tour.listings[0].id}/choose-tour-time`}
                          >
                            reschedule
                          </ButtonDeprecated>
                        </div>
                      )}

                      {tourSection.isUpcoming &&
                        recommendedListings &&
                        recommendedListings.length > 0 && (
                          <div className="tourRowsContainer top-border-divider">
                            <span className="subtitle">we recommend {homePluralize}</span>
                            {recommendedListings.map((listing) => {
                              return (
                                <SrTourListRow
                                  requestedFrom={
                                    TOUR_SHOWING_APPLICATION_SOURCE_RECOMMENDED_PAGE_TOURS
                                  }
                                  key={`tour-${tour.id}-listing-${listing.id}`}
                                  listingNumberToSee={listingNumberToSee}
                                  listing={listing}
                                  tour={tour}
                                  onConfirmRemoveLastListing={this._onOpenRemoveConfirmationModal}
                                  onClose={this._onCloseEditModal}
                                  highlight={false}
                                  isRecommendation
                                  userAgent={userAgent}
                                />
                              );
                            })}
                          </div>
                        )}

                      {tourSection.isUpcoming &&
                        !(tour.markedNeedsReschedulingFlag || !tour.startDate) && (
                          <div>
                            <SrLink className="blueBannerContainer" to="/feed">
                              <div className="textContainer">
                                <div className="bold">
                                  <span className="underline">Add homes</span> to your tour
                                </div>
                                <div className="subhead">We recommend seeing 5 homes!</div>
                              </div>
                            </SrLink>

                            <SrLink className="blueBannerContainer tour-cart clearfix" to="/feed">
                              <div className="plus-blue" />
                              <div className="textContainer">
                                <div className="bold">
                                  <span className="underline">Add homes</span> to your tour
                                </div>
                                <div className="subhead">We recommend seeing 5 homes!</div>
                              </div>
                            </SrLink>
                          </div>
                        )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {showEditModal && (
          <SrTourListRowEditModal
            {...editProps}
            isOpen={showEditModal}
            onClose={this._onCloseEditModal}
          />
        )}

        {showRemoveConfirmationModal && (
          <SrTourListRowRemoveConfirmationModal
            {...editProps}
            isOpen={showRemoveConfirmationModal}
            onClose={this._onCloseRemoveConfirmationModal}
          />
        )}
        {showRescheduleTourModal && (
          <SrTourRescheduleModal
            isOpen
            onClose={this._onCloseRescheduleTourModal}
            {...rescheduleProps}
          />
        )}
      </div>
    );
  }
}

SrTourList.displayName = 'SrTourList';

export default SrTourList;
